/**
 * Keeps every open Backoffice tab agreed on WHO is logged in.
 *
 * localStorage is shared across all tabs of an origin, but a running SPA is not: each tab keeps its own
 * components, and those hold the merchant they were rendered for. Log in as another merchant in one tab
 * and the older tab is still showing the previous one — except its API calls now go out with the NEW
 * session token, because the token is read from the shared localStorage on every call while the merchant
 * in the URL comes from what that page already had. The backend sees a request whose merchant does not
 * match the token's user and refuses it:
 *
 *     SecurityCheckInterceptor INFO Request merchant not equal the user's merchant!
 *
 * Refusing is correct — that is tenant isolation doing its job — but the request should never have been
 * made. A tab whose identity has been replaced underneath it has nothing valid left to show, so it
 * reloads (or goes to the login page if the other tab logged out).
 */
const POS_WEBVIEW_FLAG = 'posWebview';

/**
 * The pages a signed-OUT browser is allowed to sit on. A tab on one of these needs different handling
 * below: it has no merchant-scoped state to protect, so "start over" means "go in", not "reload".
 *
 * These once had to be handled with more care than that. Login.vue used to END the session on arrival
 * — on the server, revoking the token — so a reload here was a logout, and two tabs on /login could
 * cancel each other's sign-in (2026-09-19). router.beforeEach now sends a signed-in user away from the
 * login pages before they mount, so that hazard is gone; this branch remains because navigating to /
 * directly is one hop where reload-then-redirect is two.
 */
const LOGIN_PAGES = ['/login', '/superadmin/login'];

function onLoginPage() {
  try {
    return LOGIN_PAGES.includes(window.location.pathname);
  } catch (e) {
    return false;
  }
}

function readMerchant() {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return user ? user.merchant : null;
  } catch (e) {
    return null;
  }
}

function isPosWebview() {
  try {
    return sessionStorage.getItem(POS_WEBVIEW_FLAG) === '1';
  } catch (e) {
    return false;
  }
}

export function startSessionSync() {
  if (typeof window === 'undefined') return;

  // The TastyPOS refund WebView has its user injected rather than logged in, and must never be
  // navigated out from under a refund in progress.
  if (isPosWebview()) return;

  const bootMerchant = readMerchant();

  window.addEventListener('storage', (event) => {
    if (event.key !== 'user') return;   // the merchant-setting/locations refresh rewrites `user` constantly

    const current = readMerchant();
    if (current === bootMerchant) return;   // same tenant, just refreshed data

    if (isPosWebview()) return;

    if (!current) {
      // Signed out elsewhere. Leave the page rather than keep a dead session on screen — unless this
      // IS the login page, which is where a signed-out tab belongs.
      if (onLoginPage()) return;
      window.location.assign('/login');
      return;
    }

    if (onLoginPage()) {
      // Somebody signed in from another tab while this one sat on the login form. Follow that session
      // in. The form has nothing in it worth keeping, and the router would redirect a reload here to /
      // anyway — this just skips the reload.
      window.location.assign('/');
      return;
    }

    // A different merchant owns this browser now. Everything this tab is showing belongs to the
    // previous one, so start over rather than mix the two.
    window.location.reload();
  });
}

export default { startSessionSync };
